import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '@/shared/stores/auth';

/**
 * Die Wege durch das Adminportal.
 *
 * ## Warum jede Ansicht nachgeladen wird
 *
 * `() => import(...)` statt eines festen Imports: Wer sich anmeldet, lädt den
 * Anmeldebildschirm und sonst nichts. Die Flottenansicht mit ihren Diagrammen
 * kommt erst, wenn sie jemand öffnet. Bei einer Oberfläche, die über eine
 * einzige Edge ausgeliefert wird, ist das kein Feinschliff.
 *
 * ## Der Wächter prüft nichts nach
 *
 * Er entscheidet nur, **was gezeigt wird**. Die eigentliche Grenze liegt im
 * Master: Jeder Endpunkt hinter `AdminAuthGuard` weist ab, wer nicht
 * angemeldet ist. Ein Wächter im Browser, der etwas anderes zusicherte, wäre
 * eine Behauptung — man kann ihn mit der Entwicklerkonsole abschalten.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/anmelden',
    name: 'login',
    component: async () => import('@/features/auth/views/LoginView.vue'),
    meta: { public: true, title: 'Anmelden' },
  },
  {
    path: '/',
    component: async () => import('@/app/layout/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: async () => import('@/features/dashboard/views/DashboardView.vue'),
        meta: { title: 'Übersicht' },
      },
      {
        path: 'websites',
        name: 'sites',
        component: async () => import('@/features/sites/views/SitesView.vue'),
        meta: { title: 'Websites' },
      },
      {
        path: 'websites/typen',
        name: 'blueprints',
        component: async () => import('@/features/sites/views/BlueprintsView.vue'),
        meta: { title: 'Website-Typen' },
      },
      {
        path: 'websites/:siteId',
        name: 'site-detail',
        component: async () => import('@/features/sites/views/SiteDetailView.vue'),
        meta: { title: 'Website' },
      },
      {
        path: 'flotte/nodes',
        name: 'nodes',
        component: async () => import('@/features/fleet/views/NodesView.vue'),
        meta: { title: 'Nodes' },
      },
      {
        path: 'flotte/nodes/:nodeId',
        name: 'node-detail',
        component: async () => import('@/features/fleet/views/NodeDetailView.vue'),
        meta: { title: 'Node' },
      },
      {
        path: 'flotte/edges',
        name: 'edges',
        component: async () => import('@/features/fleet/views/EdgesView.vue'),
        meta: { title: 'Edges' },
      },
      {
        path: 'betrieb/operationen',
        name: 'operations',
        component: async () => import('@/features/operations/views/OperationsView.vue'),
        meta: { title: 'Operationen' },
      },
      {
        path: 'sicherung/storage-boxen',
        name: 'storage-boxes',
        component: async () => import('@/features/backup/views/StorageBoxesView.vue'),
        meta: { title: 'Storage Boxen' },
      },
      {
        path: 'verwaltung/pruefprotokoll',
        name: 'audit',
        component: async () => import('@/features/audit/views/AuditView.vue'),
        meta: { title: 'Prüfprotokoll' },
      },
      {
        path: 'verwaltung/rechtstexte',
        name: 'legal',
        component: async () => import('@/features/legal/views/LegalView.vue'),
        meta: { title: 'Rechtstexte' },
      },
      {
        path: 'konto',
        name: 'account',
        component: async () => import('@/features/account/views/AccountView.vue'),
        meta: { title: 'Mein Konto' },
      },
    ],
  },
  {
    // Alles Unbekannte führt zur Übersicht statt auf eine leere Seite. Eine
    // eigene 404-Ansicht wäre eine Seite, die niemand jemals absichtlich
    // aufruft.
    path: '/:pfad(.*)*',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Beim ersten Aufruf steht noch nicht fest, ob jemand angemeldet ist: Die
  // Sitzung liegt in einem Cookie, das kein Skript lesen kann. Also fragen.
  if (!auth.checked) {
    await auth.load().catch(() => {
      // Ein nicht erreichbarer Master ist keine Abmeldung. Die Ansicht zeigt
      // ihre eigene Meldung; der Wächter lässt hier durch, statt in den
      // Anmeldebildschirm umzuleiten und die Sitzung damit zu verlieren.
    });
  }

  if (to.meta.public === true) {
    return auth.isAuthenticated ? { name: 'dashboard' } : true;
  }

  if (!auth.isAuthenticated) {
    // Der Weg wird gemerkt: Nach der Anmeldung landet man dort, wo man hin
    // wollte — und nicht auf der Übersicht.
    return { name: 'login', query: { weiter: to.fullPath } };
  }

  return true;
});

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : null;

  document.title = title === null ? 'kiruno — Verwaltung' : `${title} — kiruno`;
});
